import {
  BoxV2 as Box,
  Flex,
  Table,
  TableFilter,
  useFilters,
  useTable,
  usePermission,
} from 'portal-commons';
import { FunctionComponent, useEffect, useState } from 'react';

import { FilterConfigs, FilterLoaders, TableHeaders } from './constants';
import RcsCampaignActionModal from '../../components/RcsCampaignActionModal';
import RcsCampaignsByCspsListingRow, {
  ActionType,
} from '../../components/RcsCampaignsByCspsListingRow';
import { campaignController } from '@/apis/core';
import { CampaignListItem } from '@/apis/core/types';
import { FilterToggleButton, Loader } from '@/shared_elements';
import {
  getRcsBillingCategoryOptions,
  getCnpListOptions,
  getRcsUseCaseOptions,
  getRcsHostingRegionOptions,
  getRejectionCategoryOptions,
} from '@/apis/services';
import { convertTimeWithTimezone } from '@/utils/time';

const RcsCampaignsByCsps: FunctionComponent = () => {
  const {
    configs: filterConfigs,
    appliedFilters,
    additionalFilters,
    candidateFilters,
    handleEdit: handleCandidateFiltersChange,
    handleApply: handleAppliedFiltersChange,
    setOptions: setFilterOptions,
  } = useFilters({
    configs: FilterConfigs,
    loaders: FilterLoaders,
  });
  const [filtersShown, setFiltersShown] = useState(false);
  const [activeCampaign, setActiveCampaign] = useState<CampaignListItem>();
  const [actionType, setActionType] = useState<ActionType>();
  const [rejectionCategoryOptions, setRejectionCategoryOptions] =
    useState<{ label: string; value: string }[]>();
  const { hasPermission } = usePermission();

  const fetchCampaigns = async () => {
    const response = await campaignController.getPrimaryDcaCampaigns({
      sharingStatus: 'PENDING,ACCEPTED',
      ...getQueries(),
      channelId: 'RCS',
    });
    return {
      data: response?.records || [],
      rowsPerPage: response?.recordsPerPage || 10,
      total: response?.totalRecords || 0,
      page: response?.page || 1,
    };
  };

  const getQueries = () => {
    const { sharedOnDate, ...restAppliedFilters } = appliedFilters;
    const [startDate, endDate] = sharedOnDate
      ? dateRangeStringToTuple(sharedOnDate)
      : [];
    return {
      ...tableFilters,
      ...appliedFilters,
      ...restAppliedFilters,
      ...additionalFilters,
      startDate,
      endDate,
    };
  };

  const dateRangeStringToTuple = (dateRange?: unknown): unknown[] => {
    if (typeof dateRange === 'string' && dateRange.includes('-')) {
      const dates = (dateRange as string).replace(/\s/gi, '').split('-');
      if (dates.length === 2) {
        return [
          convertTimeWithTimezone(dates[0], 'YYYY-MM-DD'),
          convertTimeWithTimezone(dates[1], 'YYYY-MM-DD'),
        ];
      }
    }
    return [];
  };

  const {
    loading: tableLoading,
    pageLoading,
    total: totalCampaigns,
    handleSorting: handleSortingChange,
    handleChangePage: handlePageChange,
    tableData: campaigns,
    pagination,
    filter: tableFilters,
    reload,
  } = useTable<CampaignListItem>(fetchCampaigns, {
    cappingTotal: 10000,
    deps: [appliedFilters, additionalFilters],
    defaultFilter: {
      sortField: 'sharedOnDate',
      ascendingOrder: false,
    },
  });

  const fetchFilterOptions = async () => {
    const results = await Promise.allSettled([
      getRcsBillingCategoryOptions(),
      getRcsHostingRegionOptions(),
      getRcsUseCaseOptions(),
      getCnpListOptions(),
    ]);

    results.forEach((r, index) => {
      if (r.status === 'fulfilled') {
        const filterKey = {
          0: 'billingCategory',
          1: 'hostingRegion',
          2: 'usecase',
          3: 'downstreamCnpUid',
        };
        const key = filterKey[index];
        key && setFilterOptions(key, r.value || []);
      }
    });
  };

  useEffect(() => {
    fetchFilterOptions();
    getRejectionCategoryOptions().then((options) =>
      setRejectionCategoryOptions(options)
    );
  }, []);

  const handleFiltersToggle = () => {
    setFiltersShown(!filtersShown);
  };

  const handleRowActionClick = (
    type: ActionType,
    campaign: CampaignListItem
  ) => {
    setActionType(type);
    setActiveCampaign(campaign);
  };

  const handleRowActionComplete = () => {
    setActiveCampaign(undefined);
    setActionType(undefined);
    reload();
  };

  if (pageLoading) {
    return <Loader />;
  }

  return (
    <section data-testid="rcsCampaignsByCsps">
      <Flex
        sx={{
          alignItems: 'center',
          mb: 'xs',
          fontWeight: 600,
          fontSize: 'H600',
          lineHeight: '32px',
          color: 't.black80',
        }}
      >
        <span>
          {!pageLoading && totalCampaigns !== 0 ? `${totalCampaigns} ` : ''}RCS
          Campaigns Shared by CSPs
        </span>
      </Flex>
      <Box sx={{ mt: 'xs', mb: 's' }}>
        <FilterToggleButton
          visible={filtersShown}
          onClick={handleFiltersToggle}
        />
      </Box>
      {filtersShown && (
        <Box sx={{ '.MuiAutocomplete-root': { width: 'auto' }, my: 's' }}>
          <TableFilter
            portal="dca"
            configs={filterConfigs}
            candidateValues={candidateFilters}
            appliedValues={appliedFilters}
            onCandidateValuesChange={handleCandidateFiltersChange}
            onAppliedValuesChange={handleAppliedFiltersChange}
            data-testid="rcsCampaignsByCsps"
          />
        </Box>
      )}
      <Table
        testId="rcsCampaignByCspsTable"
        headRows={TableHeaders}
        emptyState="no campaigns to view"
        loading={tableLoading}
        tableData={campaigns.map((record) => (
          <RcsCampaignsByCspsListingRow
            key={record.uid}
            campaign={record}
            onActionClick={(type) => handleRowActionClick(type, record)}
            permissions={{
              accept: hasPermission(
                'rcsCampaignByCspList.acceptCampaign'
              ) as boolean,
              decline: hasPermission(
                'rcsCampaignByCspList.declineCampaign'
              ) as boolean,
            }}
          />
        ))}
        handleChangePage={handlePageChange}
        createSortHandler={handleSortingChange}
        filter={tableFilters}
        pagination={pagination}
        headerTextTransform="none"
      />
      {activeCampaign && actionType && (
        <RcsCampaignActionModal
          campaignUid={activeCampaign.uid}
          open={!!actionType}
          type={actionType}
          decliningCampaign={{
            campaignUid: activeCampaign.uid,
            brandUid: activeCampaign.brandUid,
            downstreamCnpName: activeCampaign.downstreamCnpName,
          }}
          rejectionCategoryOptions={rejectionCategoryOptions}
          onCancel={() => {
            setActiveCampaign(undefined);
            setActionType(undefined);
          }}
          onComplete={handleRowActionComplete}
        />
      )}
    </section>
  );
};

export default RcsCampaignsByCsps;
